import { t } from "@lingui/macro";
import { useTheme } from "@shopify/restyle";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue, useSetAtom } from "jotai";
import { useState } from "react";
import { ScrollView } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useFragment } from "react-relay";

import { Button } from "@whoosh/design-system/src/components/controls";
import { Loader } from "@whoosh/design-system/src/components/display";
import { CloseIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import { PanelHeader } from "@whoosh/design-system/src/components/layout/PanelHeader";
import { Text, VStack } from "@whoosh/design-system/src/components/primitive";

import { ClubLogo } from "../../../../components/ClubLogo/ClubLogo";
import { panelHeaderTitleAtom } from "../../atoms/selectors/panelHeaderTitleAtom/panelHeaderTitleAtom";
import { isBookingWizardClosingAtom } from "../../atoms/stores";
import { WizardFooter } from "../WizardFooter/WizardFooter";
import { BookingWizardUIFooterFragment$key } from "./__generated__/BookingWizardUIFooterFragment.graphql";
import { BookingWizardUIHeaderFragment$key } from "./__generated__/BookingWizardUIHeaderFragment.graphql";

export type BookingWizardUIProps = {
  isBookingUnavailable?: boolean;
  currentServerTime: string | null | undefined;
  uiHeaderRef: BookingWizardUIHeaderFragment$key | null;
  uiFooterRef: BookingWizardUIFooterFragment$key | null;
  StepContainer: React.ReactNode;
  onDismiss: () => void;
  onCancel?: () => void;
};

/**
 * Shared UI component for both booking types
 */
export const BookingWizardUI = ({
  isBookingUnavailable = false,
  currentServerTime,
  uiHeaderRef,
  uiFooterRef,
  StepContainer,
  onDismiss,
  onCancel,
}: BookingWizardUIProps) => {
  const timeSlot = useFragment(
    BookingWizardUIFooterFragmentString,
    uiFooterRef
  );
  const club = useFragment(BookingWizardUIHeaderFragmentString, uiHeaderRef);

  const panelHeader = useAtomValue(panelHeaderTitleAtom);
  const { colors } = useTheme();
  const { bottom: bottomInset } = useSafeAreaInsets();

  const [isCompletingBooking, setIsCompletingBooking] = useState(false);
  const setIsBookingWizardClosing = useSetAtom(isBookingWizardClosingAtom);

  const handleClose = () => {
    setIsBookingWizardClosing(true);
    (onCancel ?? onDismiss)();
  };

  return (
    <VStack flex={1}>
      <PanelHeader
        paddingVariant="medium"
        header={panelHeader}
        subHeader={club?.name ?? undefined}
        outerLeftComponent={
          <Button
            testID="modal-close-btn"
            variant="secondary"
            accessibilityLabel={t`Close button`}
            onPress={handleClose}
            leftIcon={<CloseIcon />}
          />
        }
        outerRightComponent={club ? <ClubLogo clubRef={club} /> : null}
      />
      <ScrollView
        style={{ flex: 1, backgroundColor: colors["surface-secondary"] }}
        contentContainerStyle={{ paddingBottom: bottomInset + 48 }}
      >
        {isCompletingBooking ? <Loader /> : StepContainer}
        {!timeSlot && (
          <VStack flex={1} justifyContent="center" alignItems="center">
            <Text>{t`Whoops! Something went wrong.`}</Text>
          </VStack>
        )}
      </ScrollView>
      {!isBookingUnavailable && (
        <WizardFooter
          currentServerTime={currentServerTime}
          timeSlotRef={timeSlot ?? null}
          onClose={onDismiss}
          setIsCompletingBooking={setIsCompletingBooking}
        />
      )}
    </VStack>
  );
};

export const BookingWizardUIFooterFragmentString = graphql`
  fragment BookingWizardUIFooterFragment on Node {
    ...WizardFooter_Node
  }
`;

export const BookingWizardUIHeaderFragmentString = graphql`
  fragment BookingWizardUIHeaderFragment on Club {
    ...ClubLogo_Club
    name
    logoImage {
      # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
      publicId: renderingKey
    }
  }
`;
